import { Label } from '@purpur/library';
import { IconChevronDown } from '@purpur/library/icon/chevron-down';
import styled, { css } from 'styled-components';

import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurMotion,
    getPurpurShadow,
    getPurpurSpacing,
} from '@telialt/common-theme/src/purpurSelectors';

export const HiddenInput = styled.input.attrs({ type: 'hidden', readOnly: true })`
    display: block;
    width: 0;
    height: 0;
    font-size: 1px;
    margin: 0;
    padding: 0;
    border: none;
    appearance: none;

    &:focus {
        outline: none;
    }
`;

export const SingleSelectComboBoxFieldset = styled.div`
    border: none;
    position: relative;
    padding: 0;
    margin: 0;
    font-weight: normal;
`;

export const SingleSelectComboBoxLabelWrapper = styled(Label)`
    width: 100%;
`;

export const SingleSelectComboBoxLabel = styled.div`
    margin-bottom: ${getPurpurSpacing('50')};
`;

export const ComboboxLabel = styled.div``;

export const Combobox = styled.div<{ $isCompact: boolean; $isDisabled: boolean }>`
    user-select: none;
    position: relative;
    border-radius: ${getPurpurBorder('radiusSm')};
    border: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderInteractiveSubtle')};
    background: ${getPurpurColor('backgroundPrimary')};
    font-size: 1rem;
    line-height: 1.5;
    appearance: none;
    box-sizing: border-box;
    display: flex;
    margin: 0;
    padding: ${({ $isCompact }) =>
        $isCompact ? `${getPurpurSpacing('100')} ${getPurpurSpacing('200')}` : getPurpurSpacing('200')};
    width: 100%;
    cursor: pointer;

    ${ComboboxLabel} {
        flex-grow: 1;
        overflow: hidden;
    }

    ${({ $isDisabled }) =>
        $isDisabled
            ? css`
                  cursor: not-allowed;
                  color: ${getPurpurColor('textWeak')};
                  background: ${getPurpurColor('backgroundInteractiveDisabled')};
              `
            : css`
                  &:hover {
                      border-color: ${getPurpurColor('borderInteractiveSubtleHover')};
                      box-shadow: 0 0 0 1px ${getPurpurColor('purple900')};
                  }

                  &:focus,
                  &:focus-within {
                      outline: ${getPurpurBorder('widthMd')} solid ${getPurpurColor('borderInteractiveFocus')};
                      outline-offset: calc(${getPurpurSpacing('10')} * 2);
                      border-color: ${getPurpurColor('borderInteractiveSubtleHover')};
                  }
              `}
`;

export const Chevron = styled(IconChevronDown)<{ $isActive: boolean; $isDisabled: boolean }>`
    margin-left: auto;
    box-sizing: content-box;
    pointer-events: none;
    color: ${({ $isActive }) => ($isActive ? getPurpurColor('purple500') : getPurpurColor('functionalBlack'))};
    transform: rotate(${({ $isActive }) => ($isActive ? '180deg' : '0deg')});
    transition: transform ${getPurpurMotion('duration200')} ${getPurpurMotion('easingEaseInOut')};
    align-self: center;

    ${({ $isDisabled }) =>
        $isDisabled
            ? css`
                  color: ${getPurpurColor('textWeak')};
              `
            : css`
                  ${Combobox}:focus & {
                      color: ${getPurpurColor('purple500')};
                  }

                  ${HiddenInput}:invalid + & {
                      color: ${getPurpurColor('red500')};
                  }
              `}

    svg {
        width: 1.5rem;
        height: 1.5rem;
    }
`;

export const ListBox = styled.ul<{ $isVisible: boolean; $hasMaxHeight: boolean }>`
    display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
    flex-direction: column;
    padding: 0;
    margin: ${getPurpurSpacing('25')} 0 0;
    position: absolute;
    left: 0;
    right: 0;
    z-index: 1000;
    border: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderWeak')};
    border-radius: ${getPurpurBorder('radiusMd')};
    box-shadow: ${getPurpurShadow('md')};
    background-color: ${getPurpurColor('functionalWhite')};

    ${({ $hasMaxHeight }) =>
        $hasMaxHeight &&
        css`
            max-height: 20rem;
            overflow-y: auto;
        `}
`;

export const ListBoxOption = styled.li`
    user-select: none;
    padding: ${getPurpurSpacing('200')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;

    &[aria-selected='true'] {
        background-color: ${getPurpurColor('backgroundInteractivePrimary')};
        color: white;
    }

    &.focused,
    &:hover[aria-selected='false'] {
        background-color: ${getPurpurColor('backgroundInteractiveTransparentHover')};
    }

    &:hover[aria-selected='true'] {
        background-color: ${getPurpurColor('backgroundInteractivePrimaryHover')};
    }

    &:active {
        background-color: ${getPurpurColor('backgroundInteractiveTransparentActive')};
    }

    &[aria-selected='true']:active {
        background-color: ${getPurpurColor('backgroundInteractivePrimaryActive')};
    }

    &:first-child {
        border-top-right-radius: ${getPurpurBorder('radiusMd')};
        border-top-left-radius: ${getPurpurBorder('radiusMd')};
    }

    &:last-child {
        border-bottom: none;
        border-bottom-right-radius: ${getPurpurBorder('radiusMd')};
        border-bottom-left-radius: ${getPurpurBorder('radiusMd')};
    }
`;
